import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { ArrowLeft, BookOpen, Map, Users, Settings, BarChart3, MessageSquare, Puzzle, FileText, Shield, Headphones, DollarSign, PenTool } from 'lucide-react';

const APP_URL = process.env.REACT_APP_BACKEND_URL;

function GuideSection({ icon: Icon, title, children, id, theme }) {
  return (
    <div id={id} className="mb-10 rounded-xl border p-6" style={{ borderColor: theme.borderPrimary, background: 'rgba(10,8,4,0.5)' }} data-testid={`guide-${id}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${theme.accentPrimary}15`, border: `1px solid ${theme.accentPrimary}30` }}>
          <Icon className="w-5 h-5" style={{ color: theme.accentPrimary }} />
        </div>
        <h2 className="text-lg font-bold" style={{ color: theme.accentPrimary }}>{title}</h2>
      </div>
      <div className="text-sm leading-relaxed space-y-3" style={{ color: theme.textSecondary }}>
        {children}
      </div>
    </div>
  );
}

function AdminLink({ to, label, theme }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(to)}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold underline decoration-dotted underline-offset-2 hover:opacity-80 transition-colors"
      style={{ color: theme.accentPrimary }}>
      {label} &rarr;
    </button>
  );
}

export default function HelpGuide() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isAdmin = user?.email === 'cfenison@gmail.com' || user?.email === 'testadmin@test.com';

  return (
    <div className="min-h-screen" style={{ background: theme.bgPrimary, color: theme.textPrimary }}>
      <nav className="flex items-center justify-between px-6 py-4 border-b backdrop-blur-sm" style={{ borderColor: theme.borderPrimary, background: theme.bgNavbar }}>
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm" style={{ color: theme.navText }} data-testid="btn-back-home">
          <ArrowLeft className="w-4 h-4" /> Back to Map
        </button>
        <span className="text-xs tracking-widest uppercase" style={{ color: theme.textMuted }}>Help &amp; Onboarding</span>
        <div className="w-20" />
      </nav>

      <div className="max-w-3xl mx-auto px-5 py-10">

        {/* Header */}
        <div className="text-center mb-12" data-testid="help-header">
          <p className="text-xs tracking-[0.4em] uppercase mb-4" style={{ color: theme.labelColor }}>BETA TESTER GUIDE</p>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4" style={{ background: theme.accentGradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Welcome to the Kingdom Knowledge Platform
          </h1>
          <p className="text-sm max-w-lg mx-auto" style={{ color: theme.textSecondary }}>
            Everything you need to know about navigating the Treasure Map, completing lessons, and providing feedback.
          </p>
        </div>

        {/* Table of Contents */}
        <div className="mb-10 rounded-xl border p-5" style={{ borderColor: theme.borderPrimary, background: 'rgba(10,8,4,0.3)' }} data-testid="guide-toc">
          <p className="text-xs tracking-wider uppercase mb-3 font-bold" style={{ color: theme.labelColor }}>QUICK NAVIGATION</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { id: 'overview', label: 'Platform Overview' },
              { id: 'treasure-map', label: 'The Treasure Map' },
              { id: 'lessons', label: 'Learning Journey' },
              { id: 'feedback', label: 'Giving Feedback' },
              ...(isAdmin ? [
                { id: 'admin-tools', label: 'Admin Tools' },
                { id: 'content-editing', label: 'Content Editing' },
              ] : []),
            ].map(item => (
              <button key={item.id} onClick={() => document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' })}
                className="text-left text-xs px-3 py-2 rounded-lg border transition-all hover:opacity-80"
                style={{ borderColor: theme.borderPrimary, color: theme.textSecondary }}>
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* 1. Platform Overview */}
        <GuideSection icon={BookOpen} title="Platform Overview" id="overview" theme={theme}>
          <p>
            This platform is an interactive Bible study workshop built around <strong style={{ color: theme.accentPrimary }}>The King's Return</strong> and other books by the ministry. It's designed as a <strong style={{ color: theme.accentPrimary }}>Treasure Map</strong> — a guided archaeological investigation of what the Bible actually says about the return of Jesus.
          </p>
          <p>
            Every lesson is a "puzzle piece." You start with three <strong>Legend Keys</strong> (reading tools), then unlock four <strong>Corner Pieces</strong> (anchor scriptures), followed by <strong>Border Pieces</strong>, <strong>Warnings</strong>, and <strong>Landmarks</strong> that fill in the complete picture.
          </p>
          <p>
            As a beta tester, your job is to walk through the curriculum, test each lesson, and provide honest feedback using the feedback panel at the bottom of every lesson.
          </p>
        </GuideSection>

        {/* 2. The Treasure Map */}
        <GuideSection icon={Map} title="The Treasure Map" id="treasure-map" theme={theme}>
          <p>
            The <AdminLink to="/" label="Treasure Map" theme={theme} /> is the home page. It shows your complete learning journey:
          </p>
          <ul className="list-disc list-inside space-y-1.5 ml-2">
            <li><strong style={{ color: theme.accentPrimary }}>Progress Markers</strong> — The row of circular markers at the top shows every lesson. Completed ones have a checkmark. <em>Click any marker to jump directly to that lesson.</em></li>
            <li><strong style={{ color: theme.accentPrimary }}>Legend Keys (3)</strong> — Start here. The GIT (Grammar Integrity Test), the Blueprints (God's Divine Rehearsals), and Recapitulation (How Revelation Is Written). Complete them in order.</li>
            <li><strong style={{ color: theme.accentPrimary }}>Corner Pieces (4)</strong> — Four anchor scriptures that lock the frame of the puzzle. They unlock after the Legend Keys.</li>
            <li><strong style={{ color: theme.accentPrimary }}>The Journey</strong> — Border Pieces, Warnings, and Landmarks fill in the full picture. These unlock progressively.</li>
          </ul>
          <p>
            The <strong>Puzzle Board</strong> button (bottom-right corner) opens a drawer showing all puzzle piece details.
          </p>
        </GuideSection>

        {/* 3. Learning Journey */}
        <GuideSection icon={Puzzle} title="The Learning Journey" id="lessons" theme={theme}>
          <p>
            Each lesson follows a consistent pattern:
          </p>
          <ol className="list-decimal list-inside space-y-1.5 ml-2">
            <li><strong>Read the teaching</strong> — Scripture passages, explanations, and the GIT analysis.</li>
            <li><strong>Interact with the content</strong> — Expand sections, read companion appendices, view scripture links.</li>
            <li><strong>Complete the lesson</strong> — Hit the completion button at the bottom to unlock the next lesson.</li>
            <li><strong>Leave feedback</strong> — Every lesson has a <strong>Beta Feedback Panel</strong> at the bottom where you can rate and comment.</li>
          </ol>
          <p>
            <strong>Locked lessons</strong> appear dimmed with a lock icon. Complete the prerequisite lesson to unlock the next one. The order is:
          </p>
          <div className="rounded-lg border p-3 mt-2" style={{ borderColor: theme.borderPrimary, background: 'rgba(10,8,4,0.4)' }}>
            <p className="text-xs font-mono" style={{ color: theme.accentPrimary }}>
              GIT &rarr; Blueprints &rarr; Recapitulation &rarr; Corner 1 &rarr; Corner 2 &rarr; Corner 3 &rarr; Corner 4 &rarr; Border 1–6 &rarr; Warnings &rarr; Landmarks
            </p>
          </div>
        </GuideSection>

        {/* 4. Providing Feedback */}
        <GuideSection icon={MessageSquare} title="Providing Feedback" id="feedback" theme={theme}>
          <p>
            At the bottom of every lesson page, you'll find the <strong style={{ color: theme.accentPrimary }}>Beta Feedback Panel</strong>. This is critical for the ministry's development process.
          </p>
          <ul className="list-disc list-inside space-y-1.5 ml-2">
            <li><strong>Rate the lesson</strong> — How clear was the teaching? How engaging?</li>
            <li><strong>Leave comments</strong> — What confused you? What was powerful? What needs work?</li>
            <li><strong>Report issues</strong> — Typos, broken links, confusing navigation — everything helps.</li>
          </ul>
          <p>
            Your feedback is private and only visible to the ministry admin. Be honest — that's what beta testing is for.
          </p>
        </GuideSection>

        {/* 5. Admin Tools — Only visible to admin */}
        {isAdmin && (
          <>
            <GuideSection icon={Settings} title="Admin Tools Overview" id="admin-tools" theme={theme}>
              <p>Here are all the admin tools available to you and what each one does:</p>
              <div className="space-y-3 mt-2">
                <div className="rounded-lg border p-3" style={{ borderColor: theme.borderPrimary, background: 'rgba(10,8,4,0.4)' }}>
                  <div className="flex items-center justify-between">
                    <strong style={{ color: theme.accentPrimary }}>Book Publisher</strong>
                    <AdminLink to="/admin" label="Open" theme={theme} />
                  </div>
                  <p className="text-xs mt-1">Upload .docx files, create books, manage cover images, set publish status and subscription access tiers.</p>
                </div>
                <div className="rounded-lg border p-3" style={{ borderColor: theme.borderPrimary, background: 'rgba(10,8,4,0.4)' }}>
                  <div className="flex items-center justify-between">
                    <strong style={{ color: theme.accentPrimary }}>Lesson Editor</strong>
                    <AdminLink to="/admin/lessons" label="Open" theme={theme} />
                  </div>
                  <p className="text-xs mt-1">Create and manage curriculum lessons (borders, warnings, landmarks). Seed AI-generated content. Set lesson order and prerequisites.</p>
                </div>
                <div className="rounded-lg border p-3" style={{ borderColor: theme.borderPrimary, background: 'rgba(10,8,4,0.4)' }}>
                  <div className="flex items-center justify-between">
                    <strong style={{ color: theme.accentPrimary }}>Puzzle Editor</strong>
                    <AdminLink to="/admin/puzzle" label="Open" theme={theme} />
                  </div>
                  <p className="text-xs mt-1">View and edit puzzle piece metadata — titles, keywords, summaries, scripture references, colors.</p>
                </div>
                <div className="rounded-lg border p-3" style={{ borderColor: theme.borderPrimary, background: 'rgba(10,8,4,0.4)' }}>
                  <div className="flex items-center justify-between">
                    <strong style={{ color: theme.accentPrimary }}>Appendix Editor</strong>
                    <AdminLink to="/admin/appendices" label="Open" theme={theme} />
                  </div>
                  <p className="text-xs mt-1">Map book appendices to puzzle pieces. Manage companion content that links to the inner pieces of the map.</p>
                </div>
                <div className="rounded-lg border p-3" style={{ borderColor: theme.borderPrimary, background: 'rgba(10,8,4,0.4)' }}>
                  <div className="flex items-center justify-between">
                    <strong style={{ color: theme.accentPrimary }}>Site Configuration</strong>
                    <AdminLink to="/admin/site-config" label="Open" theme={theme} />
                  </div>
                  <p className="text-xs mt-1">Edit the Treasure Map landing page intro text, hero title, and call-to-action button.</p>
                </div>
                <div className="rounded-lg border p-3" style={{ borderColor: theme.borderPrimary, background: 'rgba(10,8,4,0.4)' }}>
                  <div className="flex items-center justify-between">
                    <strong style={{ color: theme.accentPrimary }}>Team Management</strong>
                    <AdminLink to="/admin/team" label="Open" theme={theme} />
                  </div>
                  <p className="text-xs mt-1">Manage team members, roles, beta tester invitations. Promote users after they complete the curriculum.</p>
                </div>
                <div className="rounded-lg border p-3" style={{ borderColor: theme.borderPrimary, background: 'rgba(10,8,4,0.4)' }}>
                  <div className="flex items-center justify-between">
                    <strong style={{ color: theme.accentPrimary }}>Analytics</strong>
                    <AdminLink to="/admin/analytics" label="Open" theme={theme} />
                  </div>
                  <p className="text-xs mt-1">View engagement metrics, lesson completion rates, and user activity.</p>
                </div>
                <div className="rounded-lg border p-3" style={{ borderColor: theme.borderPrimary, background: 'rgba(10,8,4,0.4)' }}>
                  <div className="flex items-center justify-between">
                    <strong style={{ color: theme.accentPrimary }}>Pricing Manager</strong>
                    <AdminLink to="/admin/pricing" label="Open" theme={theme} />
                  </div>
                  <p className="text-xs mt-1">Set product prices, subscription tiers, and payment configurations.</p>
                </div>
              </div>
            </GuideSection>

            <GuideSection icon={PenTool} title="Editing Lesson Content" id="content-editing" theme={theme}>
              <p>
                There are two types of lesson content on this platform:
              </p>
              <div className="space-y-3 mt-2">
                <div className="rounded-lg border p-3" style={{ borderColor: 'rgba(16,185,129,0.2)', background: 'rgba(16,185,129,0.03)' }}>
                  <strong style={{ color: '#10b981' }}>Core Lessons</strong> (GIT, Blueprints, Recapitulation, Corners)
                  <p className="text-xs mt-1">These have a dedicated content editor. Open the lesson, look for the <strong>"Edit Content"</strong> link at the top (admin-only), and you'll be taken to a form where you can edit every text section — feast descriptions, teaching paragraphs, GIT applications, and more.</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <AdminLink to="/admin/lesson-content/BLUEPRINTS" label="Edit Blueprints" theme={theme} />
                    <AdminLink to="/admin/lesson-content/GIT_101" label="Edit GIT" theme={theme} />
                    <AdminLink to="/admin/lesson-content/RECAPITULATION" label="Edit Recapitulation" theme={theme} />
                  </div>
                </div>
                <div className="rounded-lg border p-3" style={{ borderColor: 'rgba(217,119,6,0.2)', background: 'rgba(217,119,6,0.03)' }}>
                  <strong style={{ color: '#d97706' }}>Curriculum Lessons</strong> (Borders, Warnings, Landmarks)
                  <p className="text-xs mt-1">These are managed through the <AdminLink to="/admin/lessons" label="Lesson Editor" theme={theme} />. You can edit titles, scripture references, and AI-generated content. Use the "Regenerate" button to have AI rewrite a section.</p>
                </div>
              </div>
            </GuideSection>
          </>
        )}

        {/* Footer */}
        <div className="text-center pt-6 pb-12" style={{ borderTop: `1px solid ${theme.borderPrimary}` }}>
          <p className="text-xs" style={{ color: theme.textMuted }}>
            Questions? Use the feedback panel on any lesson page, or reach out to the ministry admin directly.
          </p>
          <button onClick={() => navigate('/')}
            className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold border transition-all"
            style={{ background: `${theme.accentPrimary}10`, borderColor: `${theme.accentPrimary}30`, color: theme.accentPrimary }}
            data-testid="btn-back-to-map">
            <Map className="w-4 h-4" /> Back to the Treasure Map
          </button>
        </div>
      </div>
    </div>
  );
}
